import React, { useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import "./style.scss";
import Select from "react-select";
import crossIcon from "../../../assets/icons/cross_icon_white.svg";
import customStyles from "../../../customStyles";
import { useDispatch, useSelector } from "react-redux";
import { country } from "../../../utils/constants/country";
import { addAllBanks } from "../../../utils/apis/payments";
import { returnMessages } from "../../../utils/store/reducers/Message";
import { returnErrors } from "../../../utils/store/reducers/Error";
import Spinner from "../../../reusableComponents/spinner/Spinner";

// Dynamic validation schema based on settlement type
const getValidationSchema = (settlementType) => {
  const commonSchema = {
    first_name: Yup.string().required("First Name is required"),
    last_name: Yup.string().required("Last Name is required"),
    number: Yup.string()
      .max(15, "Contact Number must be less than 15 digits")
      .required("Contact Number is required"),
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    city: Yup.string().required("City is required"),
    street_address: Yup.string().required("Street Address is required"),
    state: Yup.string().required("State is required"),
    country: Yup.string().required("Country is required"),
    zip_code: Yup.string().required("Zip Code is required"),
  };

  const schemas = {
    ach: {
      account_name: Yup.string().required("Account Name is required"),
      account_number: Yup.string().required("Account Number is required"),
      routing_number: Yup.string()
        .required("Routing Number is required")
        .min(9, "Routing Number must be exactly 9 digits"),
      account_type: Yup.string().required("Account Type is required"),
    },
    wire: {
      account_name: Yup.string().required("Account Name is required"),
      account_number: Yup.string().required("Account Number is required"),
      account_type: Yup.string().required("Account Type is required"),
      routing_number: Yup.string()
        .required("Routing Number is required")
        .min(9, "Routing Number must be exactly 9 digits"),
    },
    swift: {
      account_name: Yup.string().required("Account Name is required"),
      bic_code: Yup.string().required("BIC code is required"),
      iban_number: Yup.string().required("IBAN Number is required"),
    },
    rise: {
      email: Yup.string()
        .email("Invalid email format")
        .required("Email is required"),
    },
    wise: {
      email: Yup.string()
        .email("Invalid email format")
        .required("Email is required"),
      tag: Yup.string().required("Wise tag is required"),
    },
  };

  return Yup.object({
    ...commonSchema,
    ...(schemas[settlementType.toLowerCase()] || {}),
  });
};

const BankModal = ({
  setIsVisible,
  isVisible,
  setIsEditVisible,
  isEditVisible,
  itemValue,
  fetchPaymentMethods,
}) => {
  const { idToken } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const initialValues = {
    first_name: "",
    last_name: "",
    number: "",
    email: "",
    apartment_no: "",
    city: "",
    street_address: "",
    state: "",
    country: "",
    zip_code: "",
    account_name: "",
    account_number: "",
    account_type: "",
    routing_number: "",
    bic_code: "",
    iban_number: "",
    tag: "",
  };

  useEffect(() => {
    if (itemValue) {
      formik.setValues({
        first_name: itemValue?.address?.first_name || "",
        apartment_no: itemValue?.address?.apartment_no || "",
        last_name: itemValue?.address?.last_name || "",
        email: itemValue?.address?.email || itemValue?.email || "",
        zip_code: itemValue?.address?.zip_code || "",
        street_address: itemValue?.address?.street_address || "",
        state: itemValue?.address?.state || "",
        country: itemValue?.address?.country,
        country_code: itemValue?.address?.country_code || "",
        number: itemValue?.address?.number || "",
        city: itemValue?.address?.city || "",
        account_name: itemValue?.account_name || "",
        account_number: itemValue?.account_number || "",
        account_type: itemValue?.account_type || "",
        routing_number: itemValue?.routing_number || "",
        bic_code: itemValue?.bic_code || "",
        iban_number: itemValue?.iban_number || "",
        tag: itemValue?.tag || "",
      });
    }
  }, [itemValue]);

  const formik = useFormik({
    initialValues,
    validationSchema: getValidationSchema(itemValue?.settlement_type || ""),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setSubmitting(true);
      const payload = {
        email: values.email,
        account_name: values.account_name,
        account_number: values.account_number,
        account_type: values.account_type,
        routing_number: values.routing_number,
        bic_code: values.bic_code,
        iban_number: values.iban_number,
        // tag: values.tag,
        tag: values.tag
          ? values.tag.startsWith("@")
            ? values.tag
            : `@${values.tag}`
          : "",
        address: { ...values },
      };
      try {
        const res = await addAllBanks(
          idToken,
          payload,
          itemValue?.settlement_type?.toLowerCase()
        );
        if (res?.status < 203) {
          dispatch(returnMessages("Successfully updated", 200));
          fetchPaymentMethods();
          setIsEditVisible(false);
          setIsVisible(false);
          resetForm();
        } else {
          dispatch(
            returnErrors("Something went wrong", res.response?.status || 400)
          );
        }
      } catch (error) {
        dispatch(returnErrors("Something went wrong", 500));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const renderFields = () => {
    const settlementType = itemValue?.settlement_type.toLowerCase();
    const fields = [
      { id: "first_name", label: "First Name", type: "text" },
      { id: "last_name", label: "Last Name", type: "text" },
      { id: "number", label: "Contact Number", type: "text" },
      {
        id: "email",
        label: settlementType === "wise" ? "Wise Email" : "Email",
        type: "email",
      },
      { id: "apartment_no", label: "Apartment No.", type: "text" },
      { id: "city", label: "City", type: "text" },
      { id: "street_address", label: "Street Address", type: "text" },
      { id: "state", label: "State", type: "text" },
      { id: "zip_code", label: "Zip Code", type: "text" },
    ];

    // Add Wise Tag field only for 'wise'
    if (settlementType === "wise") {
      fields.push({
        id: "tag",
        label: "Wise Tag",
        type: "text",
      });
    }

    return fields.map((field) => (
      <label
        htmlFor={field.id}
        className="addAddress_form_input"
        key={field.id}
      >
        <span className="form_input_label">{field.label}</span>
        <input
          type={field.type}
          className="form_input_box"
          id={field.id}
          name={field.id}
          placeholder={field.label}
          // onChange={formik.handleChange}
          onChange={
            field.id === "tag"
              ? (e) => {
                  let val = e.target.value;
                  if (val && !val.startsWith("@")) {
                    val = `@${val}`;
                  }
                  formik.setFieldValue("tag", val);
                }
              : formik.handleChange
          }
          onBlur={formik.handleBlur}
          value={formik.values[field.id]}
        />
        {formik.touched[field.id] && formik.errors[field.id] ? (
          <div className="error_message">{formik.errors[field.id]}</div>
        ) : null}
      </label>
    ));
  };

  const renderAccountFields = () => {
    const settlementType = itemValue?.settlement_type.toLowerCase();

    const accountFields = [
      {
        id: "account_name",
        label: "Name of Account holder",
        type: "text",
        condition: settlementType !== "wise" && settlementType !== "rise",
      },
      {
        id: "account_number",
        label: "Account Number",
        type: "text",
        condition:
          settlementType !== "swift" &&
          settlementType !== "wise" &&
          settlementType !== "rise",
      },
      {
        id: "routing_number",
        label: "Routing Number",
        type: "text",
        condition:
          settlementType !== "rise" &&
          settlementType !== "swift" &&
          settlementType !== "wise",
      },
      {
        id: "bic_code",
        label: "Swift / BIC Code",
        type: "text",
        condition: settlementType === "swift",
      },
      {
        id: "iban_number",
        label: "Account / IBAN Number",
        type: "text",
        condition: settlementType === "swift",
      },
    ];

    // Adding account_type field
    if (
      settlementType !== "rise" &&
      settlementType !== "wise" &&
      settlementType !== "swift"
    ) {
      accountFields.push({
        id: "account_type",
        label: "Account Type",
        type: "select",
        options: [
          { label: "Savings", value: "SAVINGS" },
          { label: "Current", value: "CURRENT" },
        ],
      });
    }

    return accountFields.map((field) =>
      field.condition !== false ? (
        <label
          htmlFor={field.id}
          className="addAddress_form_input"
          key={field.id}
        >
          <span className="form_input_label">{field.label}</span>
          {field.type === "select" ? (
            <Select
              className="form_input_box react-select-container customHeight"
              classNamePrefix="react-select"
              name={field.id}
              placeholder="Select Account Type"
              styles={customStyles}
              id={field.id}
              isSearchable={true}
              value={
                formik.values[field.id]
                  ? {
                      label: formik.values[field.id],
                      value: formik.values[field.id],
                    }
                  : null
              }
              onChange={(option) =>
                formik.setFieldValue(field.id, option.value)
              }
              options={field.options}
            />
          ) : (
            <input
              type={field.type}
              className="form_input_box"
              id={field.id}
              name={field.id}
              placeholder={field.label}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values[field.id]}
            />
          )}
          {formik.touched[field.id] && formik.errors[field.id] ? (
            <div className="error_message">{formik.errors[field.id]}</div>
          ) : null}
        </label>
      ) : null
    );
  };

  return (
    <div
      className="address_modal_container edit_address_modal"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="add_address_wrapper">
        <div className="header_container">
          <div className="header_wrapper">
            <h2 style={{ textTransform: "capitalize" }}>
              {itemValue?.settlement_type} bank details
            </h2>
          </div>
          <img
            src={crossIcon}
            alt="Close"
            onClick={() => {
              if (isVisible) {
                setIsVisible(!isVisible);
              } else {
                setIsEditVisible(!isEditVisible);
              }
            }}
            style={{ cursor: "pointer" }}
          />
        </div>
        <form onSubmit={formik.handleSubmit} className="addAddress_form">
          <div className="top_section">
            {renderFields()}
            <label htmlFor="country" className="form_input_label country">
              <span className="form_input_label">Country</span>
              <Select
                className="form_input_box react-select-container customHeight"
                classNamePrefix="react-select"
                name="country"
                placeholder="Select country"
                styles={customStyles}
                id="country"
                isSearchable={true}
                value={
                  formik.values.country
                    ? {
                        label: formik.values.country,
                        value: formik.values.country,
                      }
                    : null
                }
                onChange={(option) =>
                  formik.setFieldValue("country", option.value)
                }
                options={country?.map((item) => ({
                  label: item?.country,
                  value: item?.country,
                }))}
              />
              {formik.touched.country && formik.errors.country ? (
                <div className="error_message">{formik.errors.country}</div>
              ) : null}
            </label>
          </div>
          <div className="bottom_section_addr">{renderAccountFields()}</div>
          <div className="button_wrapper">
            <button
              className="standard-button"
              type="submit"
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting ? <Spinner /> : "Submit"}
            </button>
            <button
              className="cancel_button"
              type="button"
              onClick={() => {
                if (isVisible) {
                  setIsVisible(!isVisible);
                } else {
                  setIsEditVisible(!isEditVisible);
                }
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BankModal;
